import React, { useEffect, useState } from 'react';
import Icon from '@/components/Icon';
import Topbar from '@/layout/Topbar';
import { useShell, useIsMobile } from '@/layout/AppShell';
import { useCan } from '@/auth/AuthContext';
import { useUrlState } from '@/lib/useUrlState';
import { ApiError, api } from '@/lib/api';
import PaymentDetailModal from './PaymentDetailModal';
import PaymentCreateModal from './PaymentCreateModal';
import MultiFilterChip from './MultiFilterChip';
import StudentFilterChip from './StudentFilterChip';
import DateRangePicker, { rangeForPreset } from './DateRangePicker';

/**
 * Журнал платежей. Этап 1: ручной режим. Фильтры:
 *   • статус (мульти)
 *   • тип платежа = provider (мульти)
 *   • назначение = item.kind (мульти; «Доп. услуги» раскрывается в extra_lesson + extra_camp)
 *   • ученик
 *   • даты «с» / «по»
 *
 * Все фильтры и пагинация — в URL через useUrlState.
 */
const URL_SCHEMA = {
  status:    '', // CSV
  provider:  '', // CSV
  purpose:   '', // CSV (наши «синтетические» ключи назначения; в API превращаем в kinds)
  studentId: '',
  from: '',
  to:   '',
  page: 1,
  open: '',
  create: '',
};

const STATUS_OPTIONS = [
  { v: 'draft',              label: 'Черновик' },
  { v: 'pending',            label: 'Ожидает' },
  { v: 'succeeded',          label: 'Оплачен' },
  { v: 'failed',             label: 'Ошибка' },
  { v: 'canceled',           label: 'Отменён' },
  { v: 'refund_requested',   label: 'Запрошен возврат' },
  { v: 'partially_refunded', label: 'Частично возвращён' },
  { v: 'refunded',           label: 'Возвращён' },
];

// «Тип платежа» (provider). Только 5 пользовательских вариантов;
// исторические manual/modulkassa/terminal в фильтр не выводим.
const PROVIDER_OPTIONS = [
  { v: 'cash',            label: 'Наличные' },
  { v: 'card_sbp',        label: 'Карта/СБП' },
  { v: 'yookassa',        label: 'ЮКасса' },
  { v: 'bank_transfer',   label: 'Банковский перевод' },
  { v: 'onec_correction', label: 'Корректировка 1С' },
];

// «Назначение платежа» (item.kind). «Доп. услуги» — синтетический ключ,
// раскрывается в реальные kind'ы при отправке на бэк.
const PURPOSE_OPTIONS = [
  { v: 'school_admission_fee', label: 'Вступительный взнос школа' },
  { v: 'kg_admission_fee',     label: 'Вступительный взнос сад' },
  { v: 'kg_monthly',           label: 'Ежемесячная оплата сад' },
  { v: 'school_monthly',       label: 'Ежемесячная оплата школа' },
  { v: 'extras',               label: 'Дополнительные услуги' },
];
const PURPOSE_EXPAND = {
  extras: ['extra_lesson', 'extra_camp'],
};

const STATUS_LABELS = {
  draft:               { label: 'Черновик',     cls: 'pay-st pay-st--draft' },
  pending:             { label: 'Ожидает',      cls: 'pay-st pay-st--pending' },
  succeeded:           { label: 'Оплачен',      cls: 'pay-st pay-st--ok' },
  failed:              { label: 'Ошибка',       cls: 'pay-st pay-st--err' },
  canceled:            { label: 'Отменён',      cls: 'pay-st pay-st--mute' },
  refund_requested:    { label: 'Возврат…',     cls: 'pay-st pay-st--pending' },
  partially_refunded:  { label: 'Возвращ. ч.',  cls: 'pay-st pay-st--mute' },
  refunded:            { label: 'Возвращён',    cls: 'pay-st pay-st--mute' },
};

// Подписи провайдеров для колонки «Тип платежа» в таблице — короче, чем в чипе.
const PROVIDER_LABELS_SHORT = {
  cash:            'Наличные',
  card_sbp:        'Карта/СБП',
  yookassa:        'ЮКасса',
  bank_transfer:   'Банк. перевод',
  onec_correction: 'Корр. 1С',
  // legacy — на случай старых записей
  manual:          'Ручная',
  modulkassa:      'Модулькасса',
  terminal:        'Терминал',
};

/**
 * Какой тип платежа показать в таблице. Для legacy-«modulkassa» развернём в
 * «Наличные» / «Карта/СБП» по полю onecDocumentKind (туда миграция 0050
 * положила mk_type), чтобы пользователь видел реальный канал, а не «технический»
 * источник. Так же работает и фильтр «Тип платежа» — расширяет выбор cash/card_sbp.
 */
function getDisplayProvider(p) {
  if (p.provider === 'modulkassa') {
    if (p.onecDocumentKind === 'cash') return 'Наличные';
    if (p.onecDocumentKind === 'card') return 'Карта/СБП';
  }
  return PROVIDER_LABELS_SHORT[p.provider] || p.provider;
}

// «Назначение» для отображения в таблице — короткая подпись для kind'а
// payment_items. Если у платежа несколько разных kind'ов — рисуем все
// через запятую (бывает редко, но бывает).
const KIND_LABELS_SHORT = {
  school_admission_fee: 'Вступит. (школа)',
  school_monthly:       'Ежем. (школа)',
  kg_admission_fee:     'Вступит. (сад)',
  kg_monthly:           'Ежем. (сад)',
  extra_lesson:         'Доп. занятие',
  extra_camp:           'Доп. лагерь',
  manual:               'Прочее',
  discount:             'Скидка',
  refund:               'Возврат',
};

/* ---------- helpers ---------- */
function fmtAmount(v) {
  const n = Number(v) || 0;
  return n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₽';
}
function fmtDate(iso) {
  if (!iso) return '—';
  return iso.replace('T', ' ').slice(0, 16);
}
function fmtCount(v) {
  return Number(v || 0).toLocaleString('ru-RU');
}
function fmtSum(v) {
  return Number(v || 0).toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}
const csv = (arr) => (arr && arr.length ? arr.join(',') : '');
const parseCsv = (s) => (s ? String(s).split(',').filter(Boolean) : []);

/**
 * Сводная шапка-итого над таблицей платежей. 5 категорий, в каждой
 * «Платежи N» и «Сумма XXX». Считается только по succeeded в выбранном
 * диапазоне дат (бэк сам игнорирует фильтры status/provider/kinds).
 */
function SummaryHeader({ summary }) {
  const k = summary?.byKind || {};
  const cell = (entry) => entry || { count: 0, amount: 0 };
  return (
    <div className="pay-summary-grid">
      <table>
        <colgroup>
          <col style={{ width: '100px' }} />
          <col /><col /><col /><col /><col />
        </colgroup>
        <thead>
          <tr className="pay-summary-grid__top">
            <th rowSpan={2} className="pay-summary-grid__rowlbl" />
            <th colSpan={2}>Школа</th>
            <th colSpan={2}>Детский сад</th>
            <th rowSpan={2}>Дополнительные услуги</th>
          </tr>
          <tr className="pay-summary-grid__sub">
            <th>Вступительный взнос</th>
            <th>Ежемесячная оплата</th>
            <th>Вступительный взнос</th>
            <th>Ежемесячная оплата</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="pay-summary-grid__rowlbl">Платежи</td>
            <td>{fmtCount(cell(k.school_admission_fee).count)}</td>
            <td>{fmtCount(cell(k.school_monthly).count)}</td>
            <td>{fmtCount(cell(k.kg_admission_fee).count)}</td>
            <td>{fmtCount(cell(k.kg_monthly).count)}</td>
            <td>{fmtCount(cell(k.extras).count)}</td>
          </tr>
          <tr>
            <td className="pay-summary-grid__rowlbl">Сумма</td>
            <td>{fmtSum(cell(k.school_admission_fee).amount)}</td>
            <td>{fmtSum(cell(k.school_monthly).amount)}</td>
            <td>{fmtSum(cell(k.kg_admission_fee).amount)}</td>
            <td>{fmtSum(cell(k.kg_monthly).amount)}</td>
            <td>{fmtSum(cell(k.extras).amount)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default function PaymentsPage() {
  const { tweaks, updateTweak } = useShell();
  const isMobile = useIsMobile();
  const can = useCan();
  const canWrite  = can('payments', 'write');
  const canDelete = can('payments', 'delete');

  const PER_PAGE = 1000;
  const [params, setParams] = useUrlState(URL_SCHEMA);
  const [data, setData]     = useState({ rows: [], total: 0, summary: null, filteredTotalAmount: 0 });
  const [loading, setLoad]  = useState(false);
  const [error, setError]   = useState(null);
  const [reloadTick, setReloadTick] = useState(0);

  const reload = () => setReloadTick((n) => n + 1);

  // Дефолты при первом заходе (если в URL нет параметров):
  //   • status='succeeded' — показываем только оплаченные. Так оператор не
  //     теряется в куче черновиков и pending'ов; если нужны другие — снимет
  //     галку в фильтре «Статус».
  //   • from/to = последние 7 дней.
  // Проверяем именно через window.location.search, чтобы при возврате по
  // ссылке с параметрами (например, ?status=&from=…) дефолты не перетёрлись.
  useEffect(() => {
    const hasAnyUrlParam = window.location.search.length > 1;
    const patch = {};
    if (!hasAnyUrlParam) {
      patch.status = 'succeeded';
    }
    if (!params.from && !params.to) {
      const r = rangeForPreset('last_7_days');
      if (r) { patch.from = r.from; patch.to = r.to; }
    }
    if (Object.keys(patch).length) setParams(patch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusList   = parseCsv(params.status);
  const providerList = parseCsv(params.provider);
  const purposeList  = parseCsv(params.purpose);

  const hasAnyFilter =
    statusList.length || providerList.length || purposeList.length ||
    params.studentId || params.from || params.to;

  useEffect(() => {
    // Не грузим, пока эффект-инициализатор не выставил дефолтные даты.
    // Иначе при первом заходе летит запрос без диапазона (= все платежи),
    // что пользователь видит как баг «фильтр стоит 7 дней, а тянутся все».
    if (!params.from && !params.to) return;

    setLoad(true); setError(null);

    // purpose → kinds (с учётом «extras» → extra_lesson + extra_camp)
    const kinds = purposeList.flatMap((v) => PURPOSE_EXPAND[v] || [v]);

    const qs = new URLSearchParams();
    if (statusList.length)       qs.set('status', statusList.join(','));
    if (providerList.length)     qs.set('provider', providerList.join(','));
    if (kinds.length)            qs.set('kinds', kinds.join(','));
    if (params.studentId)        qs.set('student_id', String(params.studentId));
    if (params.from)             qs.set('from', params.from);
    if (params.to)               qs.set('to', params.to);
    qs.set('page', String(params.page || 1));

    qs.set('per_page', String(PER_PAGE));
    api.get('/api/payments?' + qs.toString())
      .then((res) => setData({
        rows:                res.rows    || [],
        total:               res.total   || 0,
        summary:             res.summary || null,
        filteredTotalAmount: Number(res.filteredTotalAmount) || 0,
      }))
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'))
      .finally(() => setLoad(false));
  }, [
    params.status, params.provider, params.purpose, params.studentId,
    params.from, params.to, params.page, reloadTick,
  ]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / PER_PAGE));

  const onClearAll = () => setParams({
    status: '', provider: '', purpose: '', studentId: '',
    from: '', to: '', page: 1,
  });

  return (
    <>
      <Topbar
        breadcrumb={['Финансы', 'Платежи']}
        density={tweaks.density}
        onDensityChange={(d) => updateTweak('density', d)}
      />

      <div className="filterbar">
        <div className="filterbar__row">
          <div className="chipgroup">
            <MultiFilterChip
              label="Статус"
              values={statusList}
              options={STATUS_OPTIONS}
              onChange={(v) => setParams({ status: csv(v), page: 1 })}
            />
            <MultiFilterChip
              label="Тип платежа"
              values={providerList}
              options={PROVIDER_OPTIONS}
              onChange={(v) => setParams({ provider: csv(v), page: 1 })}
            />
            <MultiFilterChip
              label="Назначение"
              values={purposeList}
              options={PURPOSE_OPTIONS}
              onChange={(v) => setParams({ purpose: csv(v), page: 1 })}
            />
            <StudentFilterChip
              studentId={params.studentId || ''}
              onChange={(s) => setParams({ studentId: s ? String(s.id) : '', page: 1 })}
            />

            {/* Диапазон дат — пресеты + custom */}
            <DateRangePicker
              from={params.from || ''}
              to={params.to || ''}
              onChange={({ from, to }) => setParams({ from, to, page: 1 })}
            />
          </div>

          {hasAnyFilter && (
            <button className="btn btn--ghost btn--sm" onClick={onClearAll}>
              Сбросить
            </button>
          )}

          <div className="filterbar__spacer" />
          <div className="filterbar__meta">Найдено: <b>{data.total || 0}</b></div>

          <div className="filterbar__rightActions">
            <button
              className="btn btn--sm"
              onClick={() => {
                // Сбрасываем pagination и собираем те же qs, что для index
                const kinds = purposeList.flatMap((v) => PURPOSE_EXPAND[v] || [v]);
                const qs = new URLSearchParams();
                if (statusList.length)   qs.set('status',   statusList.join(','));
                if (providerList.length) qs.set('provider', providerList.join(','));
                if (kinds.length)        qs.set('kinds',    kinds.join(','));
                if (params.studentId)    qs.set('student_id', String(params.studentId));
                if (params.from)         qs.set('from', params.from);
                if (params.to)           qs.set('to',   params.to);
                window.open('/api/payments/export?' + qs.toString(), '_blank');
              }}
              title="Выгрузить текущий список в Excel"
            >
              <Icon name="download" size={13} /> Excel
            </button>
            {canWrite && (
              <button className="btn btn--primary btn--sm" onClick={() => setParams({ create: '1' })}>
                <Icon name="plus" size={13} /> Новый платёж
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="main-content-wrap">
        {error && <div style={{ padding: 16, color: 'var(--red-fg)' }}>Ошибка: {error}</div>}

        {/* Шапка-итого: сводка по kind за выбранный период (только succeeded).
            На десктопе — закреплена над таблицей. На мобиле — внутри скролла
            карточек (ниже), чтобы уезжала вверх и не «отжирала» пол-экрана. */}
        {data.summary && !isMobile && <SummaryHeader summary={data.summary} />}

        {isMobile ? (
          <div className="pay-cards">
            {data.summary && <SummaryHeader summary={data.summary} />}
            {loading && data.rows.length === 0 && (
              <div className="pay-cards__empty">Загрузка…</div>
            )}
            {!loading && data.rows.length === 0 && (
              <div className="pay-cards__empty">Платежей не найдено.</div>
            )}
            {data.rows.map((p) => {
              const s = STATUS_LABELS[p.status] || { label: p.status, cls: 'pay-st' };
              const kinds = Array.isArray(p.kinds) ? p.kinds : [];
              const kindsLabels = kinds.length > 0
                ? kinds.map((k) => KIND_LABELS_SHORT[k] || k).join(', ')
                : '';
              const tags = [getDisplayProvider(p), kindsLabels].filter(Boolean).join(' · ');
              return (
                <div key={p.id} className="pay-card" onClick={() => setParams({ open: String(p.id) })}>
                  <div className="pay-card__r1">
                    <span className="pay-card__name">{p.studentName || `#${p.studentId}`}</span>
                    <span className="pay-card__amount mono">{fmtAmount(p.amount)}</span>
                  </div>
                  <div className="pay-card__r2">
                    <span className={s.cls}>{s.label}</span>
                    <span className="pay-card__tags">{tags}</span>
                    {p.receiptUrl && (
                      <a href={p.receiptUrl} target="_blank" rel="noopener noreferrer"
                         className="pay-card__receipt" title="Открыть чек"
                         onClick={(e) => e.stopPropagation()}>
                        <Icon name="doc" size={15} />
                      </a>
                    )}
                  </div>
                  <div className="pay-card__sub">
                    {fmtDate(p.createdAt)} · ID {p.id}
                    {p.onecDocumentId ? ` · 1С ${p.onecDocumentId}` : ''}
                  </div>
                </div>
              );
            })}
            {data.rows.length > 0 && (
              <div className="pay-cards__total">
                <span>Итого ({data.total}):</span>
                <b className="mono">{fmtAmount(data.filteredTotalAmount)}</b>
              </div>
            )}
          </div>
        ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 130 }}>Дата</th>
                <th>Ученик</th>
                <th style={{ width: 130 }}>Тип</th>
                <th style={{ width: 170 }}>Назначение</th>
                <th style={{ width: 130, textAlign: 'right' }}>Сумма</th>
                <th style={{ width: 130 }}>Статус</th>
                <th style={{ width: 200 }}>Описание</th>
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {loading && data.rows.length === 0 && (
                <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: 'var(--fg-muted)' }}>Загрузка…</td></tr>
              )}
              {!loading && data.rows.length === 0 && (
                <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: 'var(--fg-muted)' }}>Платежей не найдено.</td></tr>
              )}
              {data.rows.map((p) => {
                const s = STATUS_LABELS[p.status] || { label: p.status, cls: 'pay-st' };
                const kinds = Array.isArray(p.kinds) ? p.kinds : [];
                const kindsLabels = kinds.length > 0
                  ? kinds.map((k) => KIND_LABELS_SHORT[k] || k).join(', ')
                  : '—';
                return (
                  <tr key={p.id} onClick={() => setParams({ open: String(p.id) })} style={{ cursor: 'pointer' }}>
                    <td data-label="Дата" className="mono" style={{ fontSize: 12, lineHeight: 1.35 }}>
                      <div>{fmtDate(p.createdAt)}</div>
                      <div className="muted" style={{ fontSize: 10.5 }}>
                        ID {p.id}
                        {p.onecDocumentId && (
                          <> · 1С: <span style={{ color: 'var(--fg)' }}>{p.onecDocumentId}</span></>
                        )}
                      </div>
                    </td>
                    <td data-label="Ученик">{p.studentName || `#${p.studentId}`}</td>
                    <td data-label="Тип" className="muted">{getDisplayProvider(p)}</td>
                    <td data-label="Назначение" className="muted" style={{ fontSize: 12 }} title={kinds.join(', ')}>
                      {kindsLabels}
                    </td>
                    <td data-label="Сумма" className="mono" style={{ textAlign: 'right' }}>{fmtAmount(p.amount)}</td>
                    <td data-label="Статус"><span className={s.cls}>{s.label}</span></td>
                    <td data-label="Описание" className="muted" style={{ fontSize: 12 }}>{p.description || ''}</td>
                    <td data-label="Чек" onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center' }}>
                      {p.receiptUrl ? (
                        <a
                          href={p.receiptUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Открыть чек"
                          style={{ display: 'inline-flex', color: 'var(--fg-muted)' }}
                        >
                          <Icon name="doc" size={14} />
                        </a>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {data.rows.length > 0 && (
              <tfoot>
                <tr className="pay-table-total">
                  <td colSpan={4} style={{ textAlign: 'right' }}>
                    Итого по фильтру (платежей: <b>{data.total}</b>):
                  </td>
                  <td className="mono" style={{ textAlign: 'right', fontWeight: 600 }}>
                    {fmtAmount(data.filteredTotalAmount)}
                  </td>
                  <td colSpan={3} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        )}

        <div className="table-foot">
          <span>Стр. <b>{params.page || 1}</b> из {totalPages}</span>
          <div className="table-foot__pages">
            <button className="pagebtn" disabled={(params.page || 1) <= 1} onClick={() => setParams({ page: (params.page || 1) - 1 })}>‹</button>
            <button className="pagebtn" disabled={(params.page || 1) >= totalPages} onClick={() => setParams({ page: (params.page || 1) + 1 })}>›</button>
          </div>
        </div>
      </div>

      {params.open && (
        <PaymentDetailModal
          paymentId={Number(params.open)}
          canWrite={canWrite}
          canDelete={canDelete}
          onClose={() => { setParams({ open: '' }); reload(); }}
        />
      )}

      {params.create && (
        <PaymentCreateModal
          onClose={() => setParams({ create: '' })}
          onCreated={(id) => { setParams({ create: '', open: String(id) }); reload(); }}
        />
      )}
    </>
  );
}
